import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import { ConfigProvider, Drawer as AntdDrawer, Modal as AntdModal } from 'antd';
import { Icon } from '../icons/icon.js';
import { Button } from './button.js';
import { resolveEdge, useUiDirection } from './direction.js';
import { Tooltip } from './overlays.js';
/**
 * ── A PIXEL DRAWER WIDTH IS A KIT DECISION, NOT A SCREEN'S ──────────────────
 *
 * Twenty-one drawers across the two panels asked for a fixed pixel width, from
 * 440 up to 880, and nothing clamped any of them: this wrapper passed `width`
 * straight through, and neither panel has an `.ant-drawer` rule or, between
 * them, more than two layout media queries. An 880px drawer is 69% of a 1280
 * laptop — tight but fine — and wider than the viewport on a 1024×768 tablet or
 * a 1280 window at 125% OS scaling, where the drawer body simply clips.
 *
 * The screens are not wrong to name a width: 880 for a session drilldown and
 * 440 for a quick edit is a real editorial judgement about how much a reader
 * should see at once. What none of them should have to restate is "…but never
 * wider than the window", which is the same answer every time. So the number
 * stays the caller's and the ceiling is applied here.
 *
 * `min(880px, 92vw)` rather than a stylesheet rule or a media query:
 *
 *  • it is one value, in the same place AntD already writes the width, so
 *    there is no specificity contest with `@ant-design/cssinjs`'s runtime
 *    `<head>` injection — the thing `styles/ui.css` refuses to rely on;
 *  • it is continuous, so there is no breakpoint at which a drawer jumps;
 *  • it survives the drawer's own `translateX(100%)` open animation, which is
 *    a percentage of the panel's own resolved width;
 *  • and it is in the DOM as `style="width: min(880px, 92vw)"`, which is
 *    assertable — a media query in a stylesheet is not, in jsdom.
 *
 * 92vw and not 100%: the sliver of page left showing is what says "this is an
 * overlay, the list is still behind it". A string width, a `calc()` or a
 * percentage the caller passed itself is left exactly alone.
 *
 * `Modal` needs none of this — AntD already ships
 * `max-width: calc(100vw - 32px)` on `.ant-modal` (`es/modal/style/index.js`),
 * tightening to `calc(100vw - 16px)` below `screenSMMax`. Verified in 5.29.3
 * before deciding not to add a second ceiling on top of it.
 */
const VIEWPORT_CEILING = '92vw';
const VIEWPORT_CEILING_BLOCK = '92dvh';
function clampToViewport(value, ceiling) {
    return typeof value === 'number' ? `min(${value}px, ${ceiling})` : value;
}
/*
 * ── `width` / `height` ARE OURS; AntD ONLY EVER SEES `size` ─────────────────
 *
 * AntD 6 deprecated both in favour of one `size` on the drawer's own axis, and
 * its warning tests `'width' in props`, not the value. Passing both keys —
 * even as `undefined` — printed two `[antd: Drawer] … is deprecated` console
 * errors on EVERY page, because the notifications drawer is mounted by the
 * layout. The screens keep saying `width={880}` (it is the clearer word at a
 * call site); the axis is picked here and handed over as `size`.
 */
export function Drawer({ placement = 'start', closeIcon, width, height, size, ...rest }) {
    const dir = useUiDirection();
    const edge = resolveEdge(placement, dir);
    const blockAxis = edge === 'top' || edge === 'bottom';
    return (_jsx(AntdDrawer, { ...rest, size: size ??
            (blockAxis
                ? clampToViewport(height, VIEWPORT_CEILING_BLOCK)
                : clampToViewport(width, VIEWPORT_CEILING)), placement: edge, closeIcon: closeIcon === undefined ? _jsx(CloseGlyph, {}) : closeIcon }));
}
const CLOSE_LABEL = 'بستن';
/**
 * The default close glyph, with the label tooltip every other icon-only
 * control has. AntD already wraps `closeIcon` in its own `<button>` (named by
 * the locale), so this must NOT be an `IconButton` — that would nest buttons.
 * The tooltip hangs off a span around the glyph instead. AntD clones
 * `closeIcon` with an `aria-label`; this component takes and drops it, so the
 * name stays on AntD's button where it belongs.
 */
function CloseGlyph(_props) {
    return (_jsx(Tooltip, { kind: "label", title: CLOSE_LABEL, children: _jsx("span", { className: "bk-close-glyph", children: _jsx(Icon, { name: "close", size: 20, label: CLOSE_LABEL }) }) }));
}
const KitOkContext = createContext(null);
/**
 * The footer's OK as a kit `Button`. A stable component type (it reads its
 * props from context), so a caller's `footer` that renders `<OkBtn />` keeps
 * the same `<button>` across renders.
 */
const KitOkBtn = function KitOkBtn() {
    const state = useContext(KitOkContext);
    if (!state)
        return null;
    const { text, reason, loading, okType, buttonProps, onOk } = state;
    // AntD's own mapping (`convertLegacyProps`): the legacy `danger` type is a flag.
    const typeProps = okType === 'danger' ? { danger: true } : { type: okType ?? 'primary' };
    return (_jsx(Button, { ...typeProps, loading: loading, onClick: onOk, ...buttonProps, disabledReason: reason, children: text }));
};
export function Modal({ closeIcon, okDisabledReason, footer, ...rest }) {
    const locale = useContext(ConfigProvider.ConfigContext).locale;
    const explained = okDisabledReason !== undefined && (footer === undefined || typeof footer === 'function');
    let mergedFooter = footer;
    if (explained) {
        const state = {
            text: rest.okText ?? locale?.Modal?.okText ?? 'تأیید',
            reason: okDisabledReason ? okDisabledReason : null,
            loading: rest.confirmLoading,
            okType: rest.okType,
            buttonProps: rest.okButtonProps,
            onOk: rest.onOk,
        };
        mergedFooter = (_origin, { CancelBtn }) => {
            const node = (_jsxs(_Fragment, { children: [_jsx(CancelBtn, {}), _jsx(KitOkBtn, {})] }));
            return (_jsx(KitOkContext.Provider, { value: state, children: typeof footer === 'function' ? footer(node, { OkBtn: KitOkBtn, CancelBtn }) : node }));
        };
    }
    return (_jsx(AntdModal, { ...rest, footer: mergedFooter, closeIcon: closeIcon === undefined ? _jsx(CloseGlyph, {}) : closeIcon }));
}
//# sourceMappingURL=surfaces.js.map